import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { type InventoryListByIdAPI, ListTypesAPI } from '@openx/types/inventoryList';
import { AppInventoryVersion, type AppInventoryVersionType } from '@openx/types/targeting/appInventory';
import {
  type AppBundleIdOptionPayload,
  type AppInventoryOptionPayload,
  AppInventoryType,
  InventoryContentOption,
} from '@openx/types/targeting/inventoryAndContent';
import { ListIconType, type TargetingFieldsValues } from '@openx/types/targeting/targeting';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingChildDisabledState } from '../../../../utils/hooks/useTargetingChildDisabledState';
import { mapTypeToIconType } from '../../../constants';
import type { SetInventoryListItemsDispatch } from '../../../InventoryAndContent/InventoryAndContentTopBox/constants';
import { DimensionChipLine } from '../../../shared/DimensionChip/DimensionChipLine';
import { FiltersTitle } from '../../../shared/FiltersTitle/FiltersTitle';
import { useAppInventoryInventoryListAllowed } from '../../../shared/InventoryAndContent/featureFlags';
import { getAppGroupDisplayLabel } from '../../../shared/InventoryAndContent/utils';
import { InventoryListItems } from '../../../shared/InventoryListItems/InventoryListItems';
import { TargetingDropdownSection } from '../../../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection';
import { SelectedItems } from '../../../shared/SelectedItems/SelectedItems';
import { getAppInventoryV2PreviewData } from './utils/getAppInventoryV2PreviewData';
import { isInventoryListSelectionDisabled } from './utils/isInventoryListSelectionDisabled';

type AppInventoryItemsSectionProps = {
  field?: TargetingFieldsValues;
  appInventoryContent: AppInventoryOptionPayload;
  appBundleIdContent: AppBundleIdOptionPayload;
  readonly?: boolean;
  bundleIdComparisonType: ComparisonType;
  isNotLastItem?: boolean;
  switchState?: boolean;
  showDimensionChipLine?: boolean;
  accountUid: string;
  inventoryListItems?: InventoryListByIdAPI['values'];
  setInventoryListItems: SetInventoryListItemsDispatch;
  inventoryVersion?: AppInventoryVersionType;
  onOpenDrawerClick: (option: AppInventoryType) => void;
  onRemoveAppName: () => void;
  onRemoveBundleIdManual: () => void;
  onRemoveBundleIdInventoryList: () => void;
};

export const AppInventoryV2FormPreviewItems = ({
  field,
  appInventoryContent,
  appBundleIdContent,
  readonly = false,
  bundleIdComparisonType,
  switchState: chipLineSwitchState,
  accountUid,
  inventoryListItems,
  setInventoryListItems,
  inventoryVersion,
  onOpenDrawerClick,
  onRemoveAppName,
  onRemoveBundleIdManual,
  onRemoveBundleIdInventoryList,
}: AppInventoryItemsSectionProps) => {
  const { t } = useTranslation();

  const { getDisabledStateForOption } = useTargetingChildDisabledState(field);

  const isInventoryListAllowed = useAppInventoryInventoryListAllowed();

  const appNameState = getDisabledStateForOption(InventoryContentOption.APP_GROUP);
  const appBundleIdState = getDisabledStateForOption(InventoryContentOption.APP_BUNDLE_ID);

  const inventoryListId = appBundleIdContent.inventorylist_id;
  const inventoryListCount = inventoryListItems?.length ?? 0;

  const { appNameEntries, bundleIdEntries, appNameCount, bundleIdCount, hasAppNameItems, hasBundleIdItems } =
    useMemo(() => {
      return getAppInventoryV2PreviewData({ appInventoryContent, appBundleIdContent });
    }, [appInventoryContent, appBundleIdContent]);

  const isInventoryListDisabled = useMemo(() => {
    return isInventoryListSelectionDisabled({ appNameEntries, bundleIdEntries, bundleIdComparisonType });
  }, [appNameEntries, bundleIdEntries, bundleIdComparisonType]);

  const isAppInventoryV2Only = inventoryVersion === AppInventoryVersion.APP_INVENTORY_ONLY_V2;

  const isAppNameSectionDisabled = Boolean(appNameState.isDisabled || appNameState.hasError);
  const appNameDisabledReasonMessage = appNameState.disabledReasonMessage;
  const appNameErrorReasonMessage = appNameState.errorReasonMessage;

  const isAppBundleIdSectionDisabled = Boolean(appBundleIdState.isDisabled || appBundleIdState.hasError);
  const appBundleIdSectionDisabledReasonMessage = appBundleIdState.disabledReasonMessage;
  const appBundleIdSectionErrorReasonMessage = appBundleIdState.errorReasonMessage;

  const isInventoryListSectionDisabled = isAppBundleIdSectionDisabled || isInventoryListDisabled;

  return (
    <Grid data-test="app-inventory-items-section">
      <TargetingDropdownSection
        name={t('App Name')}
        isEmpty={!hasAppNameItems}
        onOpenDrawerClick={() => onOpenDrawerClick(AppInventoryType.APP_NAME)}
        onRemove={onRemoveAppName}
        isDisabled={isAppNameSectionDisabled}
        disabledReasonMessage={appNameDisabledReasonMessage}
      >
        {hasAppNameItems && (
          <Box sx={{ mb: 2 }} data-test="app-name-items-section">
            <FiltersTitle
              onClear={onRemoveAppName}
              title={`${t('App Name')} [ ${appNameCount} ]`}
              readonly={readonly}
              disabledReasonMessage={appNameErrorReasonMessage}
            />

            {appNameEntries.map(
              ([comparisonType, items]) =>
                items?.length > 0 && (
                  <Grid
                    key={`app-name-${comparisonType}`}
                    data-test={`app-name-items-${comparisonType === ComparisonType.EQUALS ? 'allow' : 'block'}`}
                  >
                    <SelectedItems
                      filtersTitle={comparisonType === ComparisonType.EQUALS ? t('Allowed') : t('Blocked')}
                      isAddIcon={mapTypeToIconType[comparisonType] === ListIconType.INCLUDE}
                      selectedItems={items}
                      getItemLabel={getAppGroupDisplayLabel}
                      readonly={readonly}
                      handleChange={noop}
                      isRemovingAll={!readonly}
                      dataTest="app-name-item"
                    />
                  </Grid>
                )
            )}
          </Box>
        )}
      </TargetingDropdownSection>

      <DimensionChipLine showLabel={hasAppNameItems && hasBundleIdItems} switchState={chipLineSwitchState} />

      <TargetingDropdownSection
        name={t('App Bundle ID (Manually)')}
        isEmpty={!hasBundleIdItems}
        onOpenDrawerClick={() => onOpenDrawerClick(AppInventoryType.APP_BUNDLE_ID)}
        onRemove={onRemoveBundleIdManual}
        isDisabled={isAppBundleIdSectionDisabled}
        disabledReasonMessage={appBundleIdSectionDisabledReasonMessage}
      >
        {hasBundleIdItems && (
          <Box sx={{ mb: 2 }} data-test="app-bundle-id-items-section">
            <FiltersTitle
              onClear={onRemoveBundleIdManual}
              title={`${t('App Bundle ID (Manually)')} [ ${bundleIdCount} ]`}
              readonly={readonly}
              disabledReasonMessage={appBundleIdSectionErrorReasonMessage}
            />

            {bundleIdEntries.map(
              ([comparisonType, items]) =>
                items?.length > 0 && (
                  <Grid
                    key={`bundle-id-${comparisonType}`}
                    data-test={`bundle-id-items-${comparisonType === ComparisonType.EQUALS ? 'allow' : 'block'}`}
                  >
                    <SelectedItems
                      filtersTitle={comparisonType === ComparisonType.EQUALS ? t('Allowed') : t('Blocked')}
                      isAddIcon={mapTypeToIconType[comparisonType] === ListIconType.INCLUDE}
                      selectedItems={items}
                      readonly={readonly}
                      handleChange={noop}
                      isRemovingAll={!readonly}
                      dataTest="bundle-id-item"
                    />
                  </Grid>
                )
            )}
          </Box>
        )}
      </TargetingDropdownSection>

      {isInventoryListAllowed && (
        <>
          <DimensionChipLine
            showLabel={hasBundleIdItems && Boolean(inventoryListId)}
            switchState={chipLineSwitchState}
          />
          <TargetingDropdownSection
            name={t('App Bundle ID (From the Inventory List)')}
            isEmpty={!inventoryListId}
            onOpenDrawerClick={() => onOpenDrawerClick(AppInventoryType.INVENTORY_LIST)}
            isDisabled={isInventoryListSectionDisabled}
            disabledReasonMessage={appBundleIdSectionDisabledReasonMessage}
            onRemove={onRemoveBundleIdInventoryList}
          >
            {inventoryListId && (
              <Box sx={{ mb: 2 }} data-test="inventory-list-items-from-list">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: isAppInventoryV2Only ? 2 : 0 }}>
                  <FiltersTitle
                    title={`${t('App Bundle ID (From the Inventory List)')} [ ${inventoryListCount} ]`}
                    readonly={readonly}
                    disabledReasonMessage={appBundleIdSectionErrorReasonMessage}
                    sx={{ mt: isAppInventoryV2Only ? 0 : 1 }}
                  />
                </Box>
                <InventoryListItems
                  listType={ListTypesAPI.BUNDLE}
                  readonly={readonly}
                  accountUid={accountUid}
                  inventorylistId={inventoryListId}
                  setInventoryListItems={setInventoryListItems}
                  onChange={noop}
                  hideTitle
                  enableRemoveOneItem={!readonly}
                />
              </Box>
            )}
          </TargetingDropdownSection>
        </>
      )}
    </Grid>
  );
};
